import {
  can,
  type ProjectSummary,
  QUESTION_STATUSES,
  type QuestionStatus,
  REMARK_STATUSES,
  REVISION_STATUSES,
  type RemarkStatus,
  type RevisionStatus,
  type UploadState,
} from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { projectsQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Badge, type Tone, toast } from '../../ui/feedback';
import { Select } from '../../ui/form';

export const REVISION_TONE: Record<RevisionStatus, Tone> = {
  development: 'neutral',
  review: 'info',
  remarks: 'warning',
  approved: 'success',
  production: 'ink',
  annulled: 'neutral',
};

export const REMARK_TONE: Record<RemarkStatus, Tone> = {
  issued: 'info',
  in_work: 'info',
  fixed: 'warning',
  closed: 'success',
  returned: 'danger',
  disputed: 'warning',
  cancelled: 'neutral',
};

export const QUESTION_TONE: Record<QuestionStatus, Tone> = {
  pto_review: 'info',
  designer: 'warning',
  customer: 'warning',
  answered: 'info',
  accepted: 'success',
  merged: 'neutral',
  cancelled: 'neutral',
};

export function RevisionBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-ink-3">—</span>;
  const s = status as RevisionStatus;
  return <Badge tone={REVISION_TONE[s] ?? 'neutral'}>{REVISION_STATUSES[s] ?? status}</Badge>;
}

export function RemarkBadge({ status, overdue }: { status: RemarkStatus; overdue?: boolean }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      <Badge tone={REMARK_TONE[status]}>{REMARK_STATUSES[status]}</Badge>
      {overdue ? <Badge tone="danger">просрочено</Badge> : null}
    </span>
  );
}

export function QuestionBadge({ status, overdue }: { status: QuestionStatus; overdue?: boolean }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      <Badge tone={QUESTION_TONE[status]}>{QUESTION_STATUSES[status]}</Badge>
      {overdue ? <Badge tone="danger">просрочено</Badge> : null}
    </span>
  );
}

export const projectTarget = (
  p: Pick<ProjectSummary, 'id' | 'legalEntityId' | 'directionId' | 'parentId'>,
) => ({
  legalEntityId: p.legalEntityId,
  directionId: p.directionId,
  projectId: p.id,
  projectPath: [p.parentId],
});

/** Проекты, где у человека есть хотя бы одно из прав */
export function useProjectsWith(...perms: string[]) {
  const me = useMe();
  const q = useQuery(projectsQuery('active'));
  const list = (q.data ?? []).filter((p) => perms.some((perm) => can(me.access, perm, projectTarget(p))));
  return { ...q, list };
}

export function ProjectFilter({
  value,
  onChange,
  perms,
  allLabel = 'Все проекты',
}: {
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  perms: string[];
  allLabel?: string;
}) {
  const { list } = useProjectsWith(...perms);
  return (
    <Select
      aria-label="Проект"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || undefined)}
      className="h-11 md:h-10 md:w-72"
    >
      <option value="">{allLabel}</option>
      {list.map((p) => (
        <option key={p.id} value={p.id}>
          {p.code} {p.name}
        </option>
      ))}
    </Select>
  );
}

/** Загрузка файла с полями формы: поля идут перед файлом, как ждёт сервер */
export function UploadButton({
  url,
  fields,
  accept,
  label,
  onDone,
  variant = 'secondary',
  multiple = true,
}: {
  url: string;
  fields?: Record<string, string>;
  accept: string;
  label: ReactNode;
  onDone: (data: unknown) => void;
  variant?: 'secondary' | 'primary';
  multiple?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const upload = async (list: FileList) => {
    setBusy(true);
    try {
      let last: unknown = null;
      for (const f of Array.from(list)) {
        const form = new FormData();
        for (const [k, v] of Object.entries(fields ?? {})) form.append(k, v);
        form.append('file', f, f.name);
        last = await api(url, { method: 'POST', form });
      }
      toast(list.length > 1 ? `Загружено файлов: ${list.length}` : 'Файл загружен');
      onDone(last);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Не удалось загрузить файл', 'danger');
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = '';
    }
  };
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => e.target.files?.length && upload(e.target.files)}
      />
      <Button
        variant={variant}
        className="h-11 md:h-10"
        loading={busy}
        icon={<Paperclip className="size-4" />}
        onClick={() => ref.current?.click()}
      >
        {label}
      </Button>
    </>
  );
}

/** Ссылка на файл ПСД или фото: открывается через портал, который проверяет доступ и пишет журнал */
export function FileLink({
  id,
  name,
  kind = 'psd',
}: {
  id: string;
  name: string;
  kind?: 'psd' | 'supervision' | 'supervision/inspections';
}) {
  return (
    <a
      href={`/api/v1/${kind}/files/${id}`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex min-h-11 items-center break-all text-ink underline-offset-4 hover:underline md:min-h-0"
    >
      {name}
    </a>
  );
}

export const PSD_ACCEPT = '.pdf,.dwg,.dxf,.ifc,.xlsx,.xls,.zip,.rar';
export const PHOTO_ACCEPT = 'image/*,.pdf,.heic,.mp4,.mov';

/** Вкладки страницы: на телефоне — прокрутка вбок внутри полосы, высота 44 px */
export function Tabs<T extends string>({
  value,
  onChange,
  items,
}: {
  value: T;
  onChange: (v: T) => void;
  items: Array<{ value: T; label: ReactNode }>;
}) {
  return (
    <div
      role="tablist"
      className="mb-4 flex gap-1 overflow-x-auto border-b border-line [scrollbar-width:none]"
    >
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          role="tab"
          aria-selected={value === it.value}
          onClick={() => onChange(it.value)}
          className={
            value === it.value
              ? 'h-11 shrink-0 border-b-2 border-ink px-3 text-[14px] font-semibold text-ink'
              : 'h-11 shrink-0 border-b-2 border-transparent px-3 text-[14px] text-ink-2 hover:text-ink'
          }
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Загрузка файлов ПСД частями по 16 МБ с докачкой (ПД-02): до 2 ГБ; при обрыве связи та же сессия
 * продолжается с первой незагруженной части — повторно выберите тот же файл.
 */
export function ChunkedUploadButton({
  revisionId,
  sheetId,
  accept,
  label,
  multiple = true,
  onDone,
}: {
  revisionId: string;
  sheetId?: string;
  accept: string;
  label: ReactNode;
  multiple?: boolean;
  onDone: (data: unknown) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<{ name: string; pct: number } | null>(null);
  const putPart = async (uploadId: string, n: number, blob: Blob) => {
    for (let attempt = 1; ; attempt++) {
      try {
        const res = await fetch(`/api/v1/psd/uploads/${uploadId}/parts/${n}`, {
          method: 'PUT',
          headers: { 'x-epc-csrf': '1', 'content-type': 'application/octet-stream' },
          credentials: 'same-origin',
          body: blob,
        });
        if (!res.ok) {
          const e = (await res.json().catch(() => null)) as {
            error?: { code?: string; message?: string };
          } | null;
          throw new ApiError(
            res.status,
            e?.error?.code ?? 'upload',
            e?.error?.message ?? `Ошибка ${res.status}`,
          );
        }
        return;
      } catch (e) {
        // Сбой связи — ещё две попытки; ошибка сервера с ответом — сразу наверх
        if (attempt >= 3 || (e instanceof ApiError && e.status < 500)) throw e;
        await new Promise((r) => setTimeout(r, 1500 * attempt));
      }
    }
  };
  const upload = async (list: FileList) => {
    let last: unknown = null;
    try {
      for (const file of Array.from(list)) {
        setProgress({ name: file.name, pct: 0 });
        const st = await api<UploadState>(`/psd/revisions/${revisionId}/uploads`, {
          body: {
            name: file.name,
            size: file.size,
            sheetId: sheetId ?? null,
            clientKey: `${file.size}-${file.lastModified}`,
          },
        });
        const total = Math.ceil(file.size / st.partSize);
        const done = new Set(st.parts);
        for (let n = 1; n <= total; n++) {
          if (!done.has(n))
            await putPart(st.id, n, file.slice((n - 1) * st.partSize, Math.min(file.size, n * st.partSize)));
          setProgress({ name: file.name, pct: Math.round((n / total) * 100) });
        }
        last = await api(`/psd/uploads/${st.id}/complete`, { method: 'POST' });
      }
      toast(list.length > 1 ? `Загружено файлов: ${list.length}` : 'Файл загружен');
      onDone(last);
    } catch (e) {
      toast(
        `${e instanceof ApiError ? e.message : 'Связь прервалась'}. Выберите тот же файл ещё раз — загрузка продолжится с места обрыва.`,
        'danger',
      );
      if (last) onDone(last);
    } finally {
      setProgress(null);
      if (ref.current) ref.current.value = '';
    }
  };
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => e.target.files?.length && upload(e.target.files)}
      />
      <Button
        variant="secondary"
        className="h-11 md:h-10"
        loading={Boolean(progress)}
        icon={<Paperclip className="size-4" />}
        onClick={() => ref.current?.click()}
      >
        {progress ? `${progress.pct}% · ${progress.name.slice(0, 24)}` : label}
      </Button>
    </>
  );
}

/**
 * Проект по умолчанию в форме: список проектов приходит не сразу — когда он загрузится, подставляется
 * проект из контекста страницы (если на нём есть право) или первый доступный.
 */
export function useProjectDefault(
  list: Array<{ id: string }>,
  preferred: string | undefined,
  value: string,
  set: (id: string) => void,
) {
  useEffect(() => {
    if (value && list.some((p) => p.id === value)) return;
    if (!list.length) return;
    set(preferred && list.some((p) => p.id === preferred) ? preferred : list[0]!.id);
  }, [list, preferred, value, set]);
}
